import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;

let initialized = false;

// Invitation tokens are bearer credentials; never send them to analytics.
const redactClaimTokens = value => {
  if (typeof value === 'string') return value.replace(/(\/claim-startup\/)[A-Za-z0-9_-]{32,100}/g, '$1[redacted]');
  if (Array.isArray(value)) return value.map(redactClaimTokens);
  if (value && Object.prototype.toString.call(value) === '[object Object]') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, redactClaimTokens(item)]));
  return value;
};

// Self-hosted PostHog, wired for product-flow analysis rather than blanket
// session replay — this app shows sensitive startup financials (MRR, runway,
// cash reserves) on the authenticated dashboards, so recording never
// auto-starts here. It's instead started/stopped per-route by
// startSessionRecording/stopSessionRecording (see the route allowlist in
// App.js) so replay + heatmaps only ever run on public/pre-auth pages
// (landing, application forms, login/signup). maskAllInputs and the
// `.ph-mask` selector are defense in depth in case a recorded page ever
// renders something sensitive. Pageviews are captured manually (see
// capturePageview) since capture_pageview only fires on a hard page load,
// not React Router navigation.
export const initAnalytics = () => {
  if (initialized || !POSTHOG_KEY || !POSTHOG_HOST) return;
  initialized = true;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    before_send: event => redactClaimTokens(event),
    person_profiles: 'identified_only',
    disable_session_recording: true,
    autocapture: true,
    capture_pageview: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '.ph-mask',
    },
  });
};

// Starts/stops recording for the current session. Route-gated from App.js —
// call stopSessionRecording() the moment navigation enters an authenticated
// app shell that can show financials, and startSessionRecording() only on
// the public/pre-auth surface (landing, /apply, login, signup, etc).
export const startSessionRecording = () => {
  if (!initialized) return;
  posthog.startSessionRecording();
};

export const stopSessionRecording = () => {
  if (!initialized) return;
  posthog.stopSessionRecording();
};

// Gives each logged-in user a stable distinct_id across devices/sessions
// instead of PostHog's per-browser anonymous id, so their flow through the
// product can be followed as one person.
export const identifyUser = (user) => {
  if (!initialized || !user?.id) return;
  posthog.identify(user.id, {
    email: user.email,
    role: user.role,
    entity_type: user.entity_type,
    full_name: user.full_name,
  });
};

// Called on logout — clears the identified distinct_id so the next person on
// this device/browser doesn't inherit the previous user's identity.
export const resetAnalytics = () => {
  if (!initialized) return;
  posthog.reset();
};

export const capturePageview = (pathname) => {
  if (!initialized) return;
  posthog.capture('$pageview', { $current_url: window.location.origin + pathname });
};

export const captureEvent = (name, properties) => {
  if (!initialized) return;
  posthog.capture(name, properties);
};

export default posthog;
